<template>
  <div class="transactions-section">
    <b-container fluid="xl">
      <h3 class="title mb-4">Transactions</h3>

      <div class="history-table">
        <!-- Search -->
        <div class="custom-search">
          <input class="form-control custom-search-input" name="search" type="text"
            placeholder="Search Requests" v-model="searchQuery" @input="debounceSearch" />
            <div class="form-text">
              <strong>Note:</strong>
              <small> You can search ID, Request, Address, Status</small>
            </div>
        </div>

        <!-- Loading indicator -->
        <div v-if="loading" class="text-center my-5 py-5">
          <div class="spinner-border" role="status">
            <span class="visually-hidden">Loading...</span>
          </div>
        </div>

        <!-- No transactions message -->
        <div v-else-if="transactions.length === 0" class="text-center my-5 py-5">
          <p class="mb-0">No transactions found.</p>
        </div>

        <!-- Transactions table -->
        <div v-else class="table-responsive">
          <table class="table table-hover">
            <thead>
              <tr>
                <th>Transaction ID</th>
                <th>Request No.</th>
                <th>Amount</th>
                <th>Wallet Address</th>
                <th>Date</th>
                <th>Time</th>
                <th>Status</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="(txn, index) in transactions" :key="index">
                <td>{{ txn.transaction_id }}</td>
                <td>{{ txn.requestNo }}</td>
                <td>{{ txn.amount }} <small>BTC</small></td>
                <td>{{ txn.binanceId }}</td>
                <td>{{ txn.date }}</td>
                <td>{{ txn.time }}</td>
                <td>
                  <b-badge pill :variant="txn.statusClass">{{ txn.status }}</b-badge>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>

      <!-- Pagination -->
      <div class="my-4 d-flex flex-column flex-column-reverse flex-md-row justify-content-between align-items-center" v-if="transactions.length > 0">
        <div class="mt-3 mt-md-0">
          <span class="text-muted text-sm">
            Showing {{ getStartItem() }} to {{ getEndItem() }} of
            {{ meta.total }} entries
          </span>
        </div>
        <nav v-if="meta.last_page > 1">
          <ul class="pagination">
            <li class="page-item" :class="{ disabled: meta.current_page === 1 }">
              <a class="page-link" href="#" @click.prevent="changePage(meta.current_page - 1)">
                Previous
              </a>
            </li>

            <li v-for="page in getPageNumbers()" :key="page" class="page-item"
              :class="{ active: page === meta.current_page }">
              <a class="page-link" href="#" @click.prevent="changePage(page)">{{
                page
                }}</a>
            </li>

            <li class="page-item" :class="{ disabled: meta.current_page === meta.last_page }">
              <a class="page-link" href="#" @click.prevent="changePage(meta.current_page + 1)">
                Next
              </a>
            </li>
          </ul>
        </nav>
      </div>
    </b-container>
  </div>
</template>

<script>
export default {
  data() {
    return {
      transactions: [],
      searchQuery: "",
      loading: false,
      perPage: 10,
      meta: {
        current_page: 1,
        last_page: 1,
        total: 0,
        per_page: 10,
      },
      searchTimeout: null,
    };
  },
  methods: {
    async fetchTransactions(page = 1) {
      this.loading = true;

      try {
        const res = await this.$store.dispatch("user/getWalletTransactions", {
          page,
          perPage: this.perPage,
          search: this.searchQuery.trim(),
        });

        if (res && res.status) {
          this.transactions = (res.data || []).map((txn) => ({
            transaction_id: txn.transaction_id || "N/A",
            requestNo: txn.request_no || txn.id || "N/A",
            email: txn.email,
            amount:
              typeof txn.amount === "number"
                ? txn.amount.toFixed(6)
                : txn.amount,
            binanceId: txn.wallet_address,
            date: this.formatDate(txn.requested_at),
            time: this.formatTime(txn.requested_at),
            status: txn.status,
            statusClass: this.getStatusClass(txn.status),
          }));

          this.meta = res.meta || {
            current_page: 1,
            last_page: 1,
            total: this.transactions.length,
            per_page: this.perPage,
          };
        } else {
          this.transactions = [];
          console.error("Failed to load transactions:", res?.message);
        }
      } catch (e) {
        console.error("Fetch error:", e);
        this.transactions = [];
      } finally {
        this.loading = false;
      }
    },

    debounceSearch() {
      // Clear any existing timeout
      if (this.searchTimeout) {
        clearTimeout(this.searchTimeout);
      }

      // Set a new timeout to delay the search
      this.searchTimeout = setTimeout(() => {
        this.meta.current_page = 1; // Reset to first page on search
        this.fetchTransactions(1);
      }, 500); // 500ms debounce
    },

    changePage(page) {
      if (page >= 1 && page <= this.meta.last_page) {
        this.meta.current_page = page;
        this.fetchTransactions(page);
      }
    },

    getStatusClass(status) {
      switch (status) {
        case "approved":
          return "success";
        case "rejected":
          return "danger";
        default:
          return "warning";
      }
    },

    formatDate(datetime) {
      if (!datetime) return "N/A";
      return new Date(datetime).toLocaleDateString();
    },

    formatTime(datetime) {
      if (!datetime) return "N/A";
      return new Date(datetime).toLocaleTimeString([], {
        hour: "2-digit",
        minute: "2-digit",
      });
    },

    getStartItem() {
      return (this.meta.current_page - 1) * this.meta.per_page + 1;
    },

    getEndItem() {
      return Math.min(
        this.meta.current_page * this.meta.per_page,
        this.meta.total
      );
    },

    getPageNumbers() {
      const totalPages = this.meta.last_page;
      const currentPage = this.meta.current_page;

      if (totalPages <= 5) {
        return Array.from({ length: totalPages }, (_, i) => i + 1);
      }

      let startPage = Math.max(1, currentPage - 2);
      let endPage = Math.min(totalPages, startPage + 4);

      if (endPage === totalPages) {
        startPage = Math.max(1, endPage - 4);
      }

      return Array.from(
        { length: endPage - startPage + 1 },
        (_, i) => startPage + i
      );
    },
  },
  mounted() {
    this.fetchTransactions();
  },
};
</script>

<style scoped>
.transactions-section {
  background-color: #E2E6EB;
  padding: 1rem 0 3rem;
}

.transactions-section .container-xl {
  margin: 0 auto;
  max-width: 1750px;
  padding: 0 4%;
}

.transactions-section .title {
  font-size: 1.5rem;
  font-weight: 700;
  line-height: 30px;
}

.history-table {
  background-color: #ffffff;
  min-height: 400px;
  overflow: hidden;
}

.custom-search {
  align-items: center;
  border-bottom: 1px solid #C7CAD8;
  display: flex;
  justify-content: flex-start;
  padding: 1rem 2.5rem;
  width: 100%;
}

.custom-search .form-text {
  font-size: 0.75rem;
  margin-left: 1rem;
  margin-top: 0;
}

.custom-search .form-text strong {
  color: #3F4254;
  font-weight: 500;
}
.custom-search .form-text small {
  color: #afb5ce;
}

.custom-search-input {
  border: 1px solid #C7CAD8 !important;
  box-shadow: 0 0 0 3px rgba(100, 149, 237, 0.1);
  font-size: 0.875rem;
  font-weight: 400;
  height: 40px;
  max-width: 250px;
  padding: 0.5rem 1.5rem;
  transition: all 0.3s ease;
  width: 100%;
}

.custom-search-input:focus {
  border-color: #2684ff;
  box-shadow: 0 0 0 3px rgba(38, 132, 255, 0.3);
}

.custom-search-input::placeholder {
  color: #C7CAD8;
}

.table {
  color: #3F4254;
}

.table thead th {
  background-color: #ffffff;
  border: 0;
  color: #A1A5B7;
  font-size: 12px;
  font-weight: 600;
  padding-bottom: 10px;
  padding-top: 10px;
  white-space: nowrap;
}

.table thead th:first-child,
.table tbody td:first-child {
  padding-left: 1.25rem;
}

.table thead th:last-child,
.table tbody td:last-child {
  padding-right: 1.25rem;
}

.table tbody td {
  border-top: 0;
  color: #3F4254;
  font-size: 13px;
  padding-bottom: 20px;
  padding-top: 20px;
  vertical-align: middle;
}

.table-hover tbody tr:hover {
  background-color: rgba(60, 150, 240, 0.075);
}

.pagination {
  position: relative;
}

.pagination .page-item {
  margin: 0 1px;
}

.pagination .page-item .page-link {
  align-items: center;
  border-radius: 6px;
  color: #3F4254;
  display: inline-flex;
  height: 30px;
  justify-content: center;
  margin: 0;
  padding: 0.25rem;
  text-align: center;
  width: 30px;
}

.pagination .active .page-link {
  color: #3F4254;
}

.pagination .page-item:first-child .page-link,
.pagination .page-item:last-child .page-link {
  padding: 0.25rem 0.5rem;
  width: auto;
}

.pagination .disabled .page-link {
  background: none;
  color: #7E8299;
}

.text-sm {
  font-size: 14px;
}

/* Badge Commen CSS */
.badge {
  align-items: center;
  display: inline-flex;
  font-size: 13px;
  font-weight: 600;
  justify-content: center;
  line-height: 16px;
  padding: 0.5rem 1rem;
  text-align: center;
  text-transform: capitalize;
  width: 102px;
}

.badge-warning {
  background: #FCB913;
  border: 1px solid #FCB913;
  color: #3F4254;
}

.badge-danger {
  background: #F34721;
  border: 1px solid #F34721;
  color: #FFFFFF;
}

.badge-success {
  background: #83ED98;
  border: 1px solid #83ED98;
  color: #3F4254;
}

@media (max-width: 768px) {
  .custom-search {
    flex-direction: column;
    gap: 0.75rem;
    padding: 1.5rem;
  }

  .custom-search .custom-search-input {
    max-width: 100%;
  }

  .custom-search .form-text {
    margin: 0;
  }
}
</style>
